// assets/cart-upsell-picks.js
// Behaviour + analytics for the in-cart upsell test. Loaded on every page;
// the variant elements only exist when the drawer renders them.

(function () {
  const DEBUG = /[?&]tec_upsell_debug=1/.test(window.location.search);

  // Rolled out 2026-09-02: v1 is the default for everyone. The test is over, so
  // the theme no longer waits to be told which cell to be. Anything that still
  // wants the old behaviour has to ask for it by name, which keeps a kill switch
  // (set window.__TEC_UPSELL_CELL = 'control' before the drawer opens).
  function cell() {
    return window.__TEC_UPSELL_CELL === 'control' ? 'control' : 'v1';
  }

  // cart-upsell-picks.css gates the whole block on body.tec-cart-upsell-v1, which
  // used to be added by the Intelligems v1 cell. Same single source of truth as
  // cell() so the CSS and the JS can never disagree.
  (function applyCellClass() {
    function flag() {
      if (document.body && cell() === 'v1') document.body.classList.add('tec-cart-upsell-v1');
    }
    if (document.body) flag();
    else document.addEventListener('DOMContentLoaded', flag, { once: true });
  })();

  // Fires once per distinct set of shown products. The drawer re-renders on every
  // cart change, so keying on the payload avoids double counting.
  let lastImpression = null;

  // The entire body is guarded. "No analytics failure may break add-to-cart" is an
  // absolute requirement, and payload construction runs inside the add-to-cart path.
  function track(eventName, props) {
    try {
    const payload = Object.assign({ cell: cell() }, props);
    if (DEBUG) console.log('[tec-upsell]', eventName, payload);
    try {
      if (typeof window.cvg === 'function') {
        window.cvg({ method: 'event', event: eventName, properties: payload });
      }
    } catch (e) {
      if (DEBUG) console.warn('[tec-upsell] cvg failed', e);
    }
    try {
      if (Array.isArray(window.dataLayer)) {
        window.dataLayer.push(
          Object.assign({ event: eventName.toLowerCase().replace(/\s+/g, '_') }, payload)
        );
      }
    } catch (e) {
      if (DEBUG) console.warn('[tec-upsell] dataLayer failed', e);
    }
    } catch (e) {
      if (DEBUG) console.warn('[tec-upsell] track failed', e);
    }
  }

  function trackImpression(props) {
    const key = [props.cell || cell(), props.seed_group, (props.shown || []).join('+')].join('|');
    if (key === lastImpression) return;
    lastImpression = key;
    track('Viewed Cart Upsell', props);
  }

  window.tecUpsellTrack = track;
  window.tecUpsellImpression = trackImpression;

  // Runs fn once, when the cart drawer is open. If it is already open, runs immediately.
  // Returns a teardown function, or null when it ran synchronously or was refused.
  function whenDrawerOpen(el, fn) {
    // The drawer replaces these elements wholesale on every cart change. Control's caller
    // is an in-flight fetch, so it can land after its element was swapped out and after
    // disconnectedCallback already ran. Registering then would leak a listener that fires
    // against a detached element and reports products nobody is looking at.
    if (!el.isConnected) return null;
    const drawer = el.closest('cart-drawer');
    if (drawer && drawer.classList.contains('active')) {
      fn();
      return null;
    }
    const handler = () => {
      if (el.isConnected) fn();
    };
    document.addEventListener('tec:cart-drawer-open', handler, { once: true });
    return () => document.removeEventListener('tec:cart-drawer-open', handler);
  }

  window.tecUpsellWhenDrawerOpen = whenDrawerOpen;

  if (!customElements.get('cart-upsell-picks')) {
    customElements.define(
      'cart-upsell-picks',
      class CartUpsellPicks extends HTMLElement {
        connectedCallback() {
          const shown = (this.dataset.shown || '').split('|').filter(Boolean);
          if (!shown.length) return;
          // Keyed off the drawer opening, not off connect. The drawer markup is on every
          // page, so connect-time firing counted page views as impressions. Waiting for the
          // open also removes the Intelligems race: by the time a shopper clicks the cart
          // icon the cell flag has long since landed.
          this.teardownImpression = whenDrawerOpen(this, () => {
            if (cell() !== 'v1') return;
            trackImpression({
              cell: 'v1',
              shown_type: 'group',
              // "" when the evergreen pool supplied both tiles with no qualifying seed.
              seed_group: this.dataset.seed || null,
              seed_product_id: this.dataset.seedProductId || null,
              shown: shown,
              cart_value: Number(this.dataset.cartValue || 0) / 100,
            });
          });
        }

        disconnectedCallback() {
          if (this.teardownImpression) this.teardownImpression();
        }
      }
    );
  }

  if (!customElements.get('cart-upsell-pick')) {
    customElements.define(
      'cart-upsell-pick',
      class CartUpsellPick extends HTMLElement {
        connectedCallback() {
          this.submit = this.querySelector('.cart-upsell-pick__submit');
          this.picker = this.querySelector('.cart-upsell-pick__variant');
          this.priceEl = this.querySelector('.cart-upsell-pick__price');
          this.drawer = this.closest('cart-drawer');
          if (!this.submit) return;
          this.onClick = this.handleClick.bind(this);
          this.submit.addEventListener('click', this.onClick);
          if (this.picker) {
            this.onPickerChange = this.syncPrice.bind(this);
            this.picker.addEventListener('change', this.onPickerChange);
          }
        }

        disconnectedCallback() {
          if (this.submit && this.onClick) this.submit.removeEventListener('click', this.onClick);
          if (this.picker && this.onPickerChange) {
            this.picker.removeEventListener('change', this.onPickerChange);
          }
        }

        // Every picker-eligible product currently prices all of its variants identically
        // (verified against the live catalogue), so this is a no-op today. It exists so that
        // introducing a differently-priced variant later cannot silently show one price and
        // add another during a price-sensitive A/B test.
        syncPrice() {
          const opt = this.picker.selectedOptions[0];
          if (!opt) return;
          if (opt.dataset.price) this.dataset.price = opt.dataset.price;
          if (this.priceEl && opt.dataset.priceFormatted) {
            this.priceEl.textContent = opt.dataset.priceFormatted;
          }
        }

        variantId() {
          return this.picker ? this.picker.value : this.dataset.variantId;
        }

        handleClick(event) {
          event.preventDefault();
          // Defence in depth. The CSS gate already hides this tile outside the v1 cell, but
          // a wiring mistake upstream must not let a control shopper add through a surface
          // that is not part of their experience. Checked at click time, never at connect
          // time: a late Intelligems flag would otherwise permanently deaden a real v1 tile.
          if (cell() !== 'v1') return;
          if (this.submit.disabled) return;
          this.submit.disabled = true;
          this.submit.classList.add('loading');

          const parent = this.closest('cart-upsell-picks');
          const body = {
            items: [{ id: this.variantId(), quantity: 1 }],
          };
          if (this.drawer && typeof this.drawer.getSectionsToRender === 'function') {
            body.sections = this.drawer.getSectionsToRender().map((s) => s.id);
            body.sections_url = window.location.pathname;
            this.drawer.setActiveElement(document.activeElement);
          }

          fetch(window.routes.cart_add_url, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              // Without Accept, /cart/add answers with an HTML redirect: the add succeeds
              // but response.json() throws, so the drawer never re-renders and the
              // Added Cart Upsell event never fires. Found in browser QA 2026-08-23.
              Accept: 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify(body),
          })
            .then((r) => r.json())
            .then((response) => {
              // cart/add.js answers an error with a body carrying an HTTP-ish status.
              // Adding nothing is not a conversion, so do not report one.
              if (response && response.status && Number(response.status) >= 400) {
                this.submit.disabled = false;
                this.submit.classList.remove('loading');
                return;
              }
              // A cart with no qualifying seed still renders evergreen-pool tiles by
              // design (spec, Picking step 5), so `data-seed` can legitimately be "".
              // Normalise to 'none' or combo_key becomes ">travel-case" and the analysis
              // has to special-case a leading separator.
              const seedGroup = (parent && parent.dataset.seed) || null;
              track('Added Cart Upsell', {
                seed_group: seedGroup,
                upsold_group: this.dataset.group,
                upsold_product_id: this.dataset.productId,
                upsold_variant_id: this.variantId(),
                combo_key: (seedGroup || 'none') + '>' + this.dataset.group,
                position: Number(this.dataset.position || 0),
                price: Number(this.dataset.price || 0) / 100,
                cart_value: parent ? Number(parent.dataset.cartValue || 0) / 100 : null,
              });

              // `publish` and `PUB_SUB_EVENTS` are declared at the top level of
              // assets/cart-drawer.js, a CLASSIC script. `const PUB_SUB_EVENTS` creates a
              // global lexical binding, NOT a property of window, so `window.PUB_SUB_EVENTS`
              // is always undefined. Use the bare-identifier idiom the theme already uses
              // in assets/ab-sidecart-free-gift.js and assets/sale-july4-gwp.js.
              if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
                publish(PUB_SUB_EVENTS.cartUpdate, {
                  source: 'cart-upsell-pick',
                  cartData: response,
                });
              }
              if (this.drawer && typeof this.drawer.renderContents === 'function') {
                this.drawer.renderContents(response);
              }
            })
            .catch((e) => {
              console.error('[tec-upsell] add failed', e);
              this.submit.disabled = false;
              this.submit.classList.remove('loading');
            });
        }
      }
    );
  }
})();
